import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase/client';

interface AdminRouteProps {
  children: ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading: isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState<boolean>(true);
  
  // Force re-verification on every navigation/render 
  useEffect(() => {
    // Reset admin check when path changes or auth state changes
    if (location.pathname.startsWith('/admin')) {
      setIsCheckingRole(true);
      if (!isAuthenticated) {
        console.log('User not authenticated on admin route, redirecting');
        setIsAdmin(false);
      }
    }
  }, [location.pathname, isAuthenticated]);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsCheckingRole(false);
        return;
      }

      try {
        // Query team_members table to check if user has admin role in any team
        if (!supabase) {
          console.error('Supabase client not initialized');
          setIsAdmin(false);
          setIsCheckingRole(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('team_members')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .limit(1);

        if (error) throw error;
        
        setIsAdmin(data && data.length > 0);
      } catch (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    if (isCheckingRole && user) {
      checkAdminRole();
    }
  }, [user, isCheckingRole]);

  // Show loading state while checking authentication and role
  if (isLoading || isCheckingRole) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-primary animate-pulse"></div>
          <div className="w-4 h-4 rounded-full bg-primary animate-pulse delay-300"></div>
          <div className="w-4 h-4 rounded-full bg-primary animate-pulse delay-600"></div>
        </div>
        <p className="mt-4 text-gray-600">Verifying permissions...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Store the attempted URL for redirection after login
    sessionStorage.setItem('redirectTo', location.pathname);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to unauthorized page if not an admin
  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  // User is authenticated and has admin role
  return <>{children}</>;
};

export default AdminRoute; 